/**
 * State and Firestore actions for the CMM order notes side panel.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { CmmOrdersTableDataModel } from "../../../data-model/tablesData";
import {
  CmmNotesAndHistoryDoc,
  fetchCmmNotesAndHistory,
  saveCmmNotesAndHistoryEntry,
} from "../services/cmmOrderNotesHistoryService";
import { MAX_CMM_NOTE_CHARS } from "../utils/cmmOrderNotesDisplayUtils";

export interface UseCmmOrderNotesSidebarParams {
  /** Whether the side panel is open (triggers load when true). */
  isOpen: boolean;
  /** Selected table row; `id` scopes the Firestore `notes_and_history` path. */
  row: CmmOrdersTableDataModel | null;
  /**
   * Display label for the signed-in user stored as Firestore `poc` (e.g. profile
   * first + last name, falling back to email then id when names are unavailable).
   */
  loggedInUserName?: string | null;
}

/**
 * Manages draft text, Firestore-backed notes list, load/save lifecycle, and validation flags.
 * Exposes the order row's `notes` as a read-only client note; the composer starts empty.
 *
 * @param params - Panel visibility, row, and logged-in user label for `poc`.
 * @returns Draft state, client note, notes data, async handlers, and derived flags for the UI.
 */
export function useCmmOrderNotesSidebar(params: UseCmmOrderNotesSidebarParams) {
  const { isOpen, row, loggedInUserName } = params;
  const [draftNote, setDraftNote] = useState("");
  const [notes, setNotes] = useState<CmmNotesAndHistoryDoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const workflowDocId = useMemo(() => row?.id?.trim() ?? "", [row?.id]);
  /** `poc` on the Firestore doc — always the signed-in user (never `row.username`). */
  const poc = useMemo(() => loggedInUserName?.trim() ?? "", [loggedInUserName]);
  /** Prefilled order-level note shown read-only above the composer. */
  const clientNote = useMemo(() => {
    const raw = typeof row?.notes === "string" ? row.notes.trim() : "";
    return raw;
  }, [row?.notes]);

  const loadNotes = useCallback(async () => {
    if (!workflowDocId) {
      setNotes([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await fetchCmmNotesAndHistory(workflowDocId);
      setNotes(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load notes");
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [workflowDocId]);

  useEffect(() => {
    if (!isOpen || !workflowDocId) {
      return;
    }
    void loadNotes();
  }, [isOpen, workflowDocId, loadNotes]);

  /**
   * Clears the composer and error state when the panel closes or the selected order changes.
   */
  useEffect(() => {
    setDraftNote("");
    setError(null);
  }, [isOpen, workflowDocId]);

  const trimmedDraft = draftNote.trim();
  const charCount = draftNote.length;
  const canSave =
    trimmedDraft.length > 0 &&
    charCount <= MAX_CMM_NOTE_CHARS &&
    !saving &&
    Boolean(workflowDocId);

  const handleSave = useCallback(async () => {
    if (!canSave || !workflowDocId) return;
    setSaving(true);
    setError(null);
    try {
      await saveCmmNotesAndHistoryEntry(workflowDocId, {
        notes: trimmedDraft.slice(0, MAX_CMM_NOTE_CHARS),
        poc,
      });
      setDraftNote("");
      await loadNotes();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save note");
    } finally {
      setSaving(false);
    }
  }, [canSave, loadNotes, poc, trimmedDraft, workflowDocId]);

  return {
    draftNote,
    setDraftNote,
    clientNote,
    notes,
    loading,
    saving,
    error,
    workflowDocId,
    poc,
    charCount,
    canSave,
    handleSave,
    row,
    maxNoteChars: MAX_CMM_NOTE_CHARS,
  };
}
